import { CommonRegistry } from '../components/components';
import {
  CommonFilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
} from './SystemBase';

/**
 * Updates the Facing component based on movement intent (Input component).
 *
 * Only updates when there's active directional input - preserves the current
 * facing direction when no input is active. This means:
 * - Pushing against a wall -> you face that direction (intent-based)
 * - Knockback or external forces don't change facing direction
 *
 * Priority: vertical movement first (down > up > left > right)
 *
 * Order: After InputSyncSystem, before animation systems.
 */
export class FacingDirectionSystem extends CommonFilteredComponentSystem {
  public componentClasses: (keyof CommonRegistry)[] = ['Input', 'Facing'];
  public excludedComponentClasses: (keyof CommonRegistry)[] = ['InConversation', 'InBed'];
  protected simulationBound = true;
  protected override skipDeadEntities = true;

  updateEntity({ world, entityId }: FilteredComponentSystemUpdateEntityArgs<CommonRegistry>): void {
    const input = world.getComponent(entityId, 'Input')!;
    const facing = world.getComponent(entityId, 'Facing')!;

    // No directional input - keep current facing
    if (!input.up && !input.down && !input.left && !input.right) {
      return;
    }

    // Determine new direction - prioritize horizontal movement
    let newDirection: 'up' | 'down' | 'left' | 'right';
    if (input.left) {
      newDirection = 'left';
    } else if (input.right) {
      newDirection = 'right';
    } else if (input.down) {
      newDirection = 'down';
    } else {
      newDirection = 'up';
    }

    // Only update if direction changed
    if (facing.direction === newDirection) {
      return;
    }

    this.updateComponent(entityId, facing, { direction: newDirection });
  }
}
